"use client";

import { useState, useRef, useEffect } from "react";
import { m as motion, AnimatePresence } from "framer-motion";
import {
  Terminal as TerminalIcon,
  Hash,
  ChevronRight,
  X,
  Command,
} from "lucide-react";
import { useRouter, usePathname } from "next/navigation";
import { isConversionPage } from "@/lib/utils/conversion-pages";
import {
  buildSearchIndex,
  searchContent,
  SearchableItem,
} from "@/lib/search-index";
import { HERO_DATA, CONTACT_DATA, ABOUT_DATA } from "@/lib/data";
import { SEO_CONFIG } from "@/lib/seo-config";

// --- Smart AI Response Formatter ---
const formatAiResponse = (text: string): React.ReactNode => {
  const lines = text.split("\n");
  const elements: React.ReactNode[] = [];

  const renderInline = (str: string) => {
    // Handle **bold**
    let parts = str.split(/(\*\*[^*]+\*\*)/g);
    let rendered = parts.map((part, i) => {
      if (part.startsWith("**") && part.endsWith("**")) {
        return <span key={i} className="font-bold text-primary">{part.slice(2, -2)}</span>;
      }
      return part;
    });

    // Handle _italic_ on the already split parts
    return rendered.map((nodes, idx) => {
      if (typeof nodes === "string") {
        const subParts = nodes.split(/(_[^_]+_)/g);
        return subParts.map((subPart, subIdx) => {
           if (subPart.startsWith("_") && subPart.endsWith("_")) {
             return <span key={`${idx}-${subIdx}`} className="italic text-muted-foreground/80">{subPart.slice(1, -1)}</span>;
           }
           return subPart;
        });
      }
      return nodes;
    });
  };

  lines.forEach((line, idx) => {
    const trimmed = line.trim();
    if (!trimmed) {
      elements.push(<div key={`br-${idx}`} className="h-2" />);
      return;
    }

    // Headings (### Title)
    if (trimmed.startsWith("###")) {
      elements.push(
        <div key={`h-${idx}`} className="text-cyan-400 font-bold mt-3 mb-1 text-[11px] uppercase tracking-wide">
          {trimmed.replace(/^###\s*/, "")}
        </div>
      );
    }
    // Bullet points (• Item or - Item) - now handles leading whitespace
    else if (/^\s*[•-]\s/.test(line)) {
       const content = trimmed.replace(/^[•-]\s*/, "");
       elements.push(
         <div key={`li-${idx}`} className="flex items-start gap-2 pl-2">
           <span className="text-primary/60 mt-0.5">•</span>
           <span className="flex-1">{renderInline(content)}</span>
         </div>
       );
    }
    // Standard text
    else {
      elements.push(
        <div key={`p-${idx}`} className="text-muted-foreground/90 leading-relaxed">
          {renderInline(trimmed)}
        </div>
      );
    }
  });

  return <div className="space-y-0.5 selection:bg-primary/20 selection:text-primary">{elements}</div>;
};

// --- Chatbot Response Engine ---
import { getSmartResponse } from "@/lib/terminal-intelligence";

// --- Smart Response Handler ---
const handleSmartResponse = (input: string): React.ReactNode => {
  const responseText = getSmartResponse(input);
  return formatAiResponse(responseText);
};

// --- Configuration ---
const AVAILABLE_ROUTES = [
  { name: "home", path: "/" },
  { name: "projects", path: "/projects" },
  { name: "apps", path: "/apps" },
  { name: "blogs", path: SEO_CONFIG.blogDomain },
  { name: "resume", path: "/resume" },
  { name: "contact", path: "/contact" },
  { name: "privacy", path: "/privacy-policy" },
  { name: "disclaimer", path: "/disclaimer" },
];

const PAGE_SECTIONS = [
  { name: "hero", label: "Hero", anchor: "#hero" },
  { name: "work", label: "Work", anchor: "#work" },
  { name: "about", label: "About", anchor: "#about" },
  { name: "projects", label: "Projects", anchor: "#projects" },
  { name: "skills", label: "Skills", anchor: "#skills" },
  { name: "experience", label: "Experience", anchor: "#experience" },
  { name: "education", label: "Education", anchor: "#education" },
  { name: "contact", label: "Contact", anchor: "#contact" },
];

const ALL_COMMANDS = ["ls", "cd", "find", "search", "clear", "help"];

export function FloatingTerminal() {
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [isMaximized, setIsMaximized] = useState(false);
  const [history, setHistory] = useState<
    { id: number; input: string; output: React.ReactNode }[]
  >([]);
  const [input, setInput] = useState("");
  const [commandHistory, setCommandHistory] = useState<string[]>([]);
  const [historyIndex, setHistoryIndex] = useState<number | null>(null);
  const [suggestion, setSuggestion] = useState("");
  const [pendingNavigation, setPendingNavigation] = useState<{
    anchor: string;
    displayText: string;
  } | null>(null);
  const [pendingMatches, setPendingMatches] = useState<SearchableItem[]>([]);
  const [searchIndex, setSearchIndex] = useState<SearchableItem[]>([]);
  const [userProfile, setUserProfile] = useState("new_user");
  const [isAiLoading, setIsAiLoading] = useState(false);
  const terminalRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const router = useRouter();
  const pathname = usePathname();
  const [isBlogSubdomain, setIsBlogSubdomain] = useState(false);

  useEffect(() => {
    // Check if we're on blog subdomain
    if (typeof window !== "undefined") {
      const hostname = window.location.hostname;
      setIsBlogSubdomain(
        hostname.startsWith("blogs.") || hostname.includes(".blogs.")
      );
    }
  }, []);

  const isAdminPage = pathname.startsWith("/sachinsharma/official/adminpanel");
  const isBlogPage = pathname.startsWith("/blogs") || isBlogSubdomain;
  const isConversion = isConversionPage(pathname);

  // === Build search index on mount ===
  useEffect(() => {
    // Wait for DOM to be fully loaded
    const timer = setTimeout(() => {
      const index = buildSearchIndex();
      setSearchIndex(index);
    }, 100);

    return () => clearTimeout(timer);
  }, []);

  // --- Smart Suggestion Logic (Inline Ghost) ---
  useEffect(() => {
    if (!input) {
      setSuggestion("");
      return;
    }

    const parts = input.split(" ");
    const cmd = parts[0].toLowerCase();
    const arg = parts.slice(1).join(" ");

    if (parts.length === 1) {
      // Base command match
      const match = ALL_COMMANDS.find((c) => c.startsWith(cmd));
      setSuggestion(match || "");
    } else if (cmd === "cd") {
      // Section/Route match
      const allTargets = [
        ...AVAILABLE_ROUTES.map((r) => r.name),
        ...PAGE_SECTIONS.map((s) => s.name),
      ];
      const match = allTargets.find((t) => t.startsWith(arg));
      setSuggestion(match ? `cd ${match}` : "");
    } else {
      setSuggestion("");
    }
  }, [input]);

  const executeCommand = async (raw: string) => {
    // Save command to history if not empty
    if (raw.trim()) {
      setCommandHistory((prev) => [...prev, raw]);
    }
    setHistoryIndex(null);
    const cmdInput = raw.trim().toLowerCase();
    if (!cmdInput) return;

    const [base, ...args] = cmdInput.split(" ");
    const arg = args.join(" ");
    let result: React.ReactNode;

    // --- Handle numbered selection for multiple matches ---
    if (pendingMatches.length > 1) {
      const num = parseInt(base);
      if (!isNaN(num) && num > 0 && num <= pendingMatches.length) {
        const selected = pendingMatches[num - 1];
        setIsOpen(false);
        document
          .querySelector(selected.anchor)
          ?.scrollIntoView({ behavior: "smooth" });
        setPendingMatches([]);
        return;
      } else if (base === "n" || base === "no") {
        setPendingMatches([]);
        result = <span className="text-muted-foreground/70">Cancelled.</span>;
        setHistory((prev) => [
          ...prev,
          { id: Date.now(), input: raw, output: result },
        ]);
        return;
      }
    }

    // --- Handle Y/N responses for single pending navigation ---
    if (pendingNavigation && (base === "y" || base === "yes")) {
      setIsOpen(false);
      document
        .querySelector(pendingNavigation.anchor)
        ?.scrollIntoView({ behavior: "smooth" });
      setPendingNavigation(null);
      return;
    } else if (pendingNavigation && (base === "n" || base === "no")) {
      setPendingNavigation(null);
      result = <span className="text-muted-foreground/70">Cancelled.</span>;
      setHistory((prev) => [
        ...prev,
        { id: Date.now(), input: raw, output: result },
      ]);
      return;
    }

    switch (base) {
      case "help":
        result = (
          <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-[10px] opacity-80">
            <div className="text-primary font-bold">ls</div>
            <div className="text-muted-foreground">
              List all pages & sections
            </div>
            <div className="text-primary">cd [dir]</div>
            <div className="text-muted-foreground">Navigate to section/page</div>
            <div className="text-primary">cat [file]</div>
            <div className="text-muted-foreground">Read content (e.g., about)</div>
            <div className="text-primary">find [txt]</div>
            <div className="text-muted-foreground">
              Search portfolio content
            </div>
            <div className="text-primary">ask [q]</div>
            <div className="text-muted-foreground">Ask the dev persona</div>
            <div className="col-span-2 text-muted-foreground/50 italic mt-2">
              Tip: Standard Linux commands supported (uname, whoami, date, top...)
            </div>
          </div>
        );
        break;

      case "find":
      case "search":
        if (!arg) {
          result = (
            <span className="text-destructive">Usage: find [keyword]</span>
          );
        } else {
          const matches = searchContent(arg, searchIndex);
          if (matches.length === 0) {
            result = (
              <span className="text-muted-foreground/70">
                No matches found for "{arg}".
              </span>
            );
          } else {
            result = (
              <div className="space-y-3 text-[10px]">
                {matches.map((match, idx) => {
                  // Find the first occurrence of the search word
                  const regex = new RegExp(`(.{0,40})(${arg})(.{0,40})`, "i");
                  const previewMatch = match.content.match(regex);
                  let preview = match.content.slice(0, 100);
                  if (previewMatch) {
                    preview = `${previewMatch[1]}<b>${previewMatch[2]}</b>${previewMatch[3]}`;
                  }
                  return (
                    <div key={match.id} className="space-y-1">
                      <div className="text-green-500 font-bold">
                        [{idx + 1}] Found in {match.type.toUpperCase()}
                      </div>
                      <div className="pl-3 text-primary/80">
                        {match.displayText}
                      </div>
                      <div
                        className="pl-3 text-muted-foreground/60 italic"
                        dangerouslySetInnerHTML={{ __html: preview + "..." }}
                      />
                    </div>
                  );
                })}
                {matches.length === 1 && (
                  <div className="pt-2 text-muted-foreground/70">
                    Do you want to go to{" "}
                    <span className="text-primary font-bold">
                      {matches[0].displayText}
                    </span>
                    ? <span className="text-green-500 font-bold">(Y/N)</span>
                  </div>
                )}
                {matches.length > 1 && (
                  <div className="pt-2 text-muted-foreground/70">
                    Type <span className="text-primary font-bold">1</span>-
                    <span className="text-primary font-bold">
                      {matches.length}
                    </span>{" "}
                    to select, or{" "}
                    <span className="text-primary font-bold">n</span> to cancel
                  </div>
                )}
              </div>
            );
            if (matches.length >= 1) {
              if (matches.length === 1) {
                setPendingNavigation({
                  anchor: matches[0].anchor,
                  displayText: matches[0].displayText,
                });
              } else {
                setPendingMatches(matches);
              }
            }
          }
        }
        break;
      case "ls":
        result = (
          <div className="text-[10px] space-y-2 opacity-70">
            <div className="text-primary/60">Routes:</div>
            <div className="flex flex-wrap gap-x-4 gap-y-1 pl-3">
              {AVAILABLE_ROUTES.map((r) => (
                <div key={r.name} className="text-primary/50">
                  /{r.name}
                </div>
              ))}
            </div>
            <div className="text-primary/60 mt-2">Sections:</div>
            <div className="flex flex-wrap gap-x-4 gap-y-1 pl-3">
              {PAGE_SECTIONS.map((s) => (
                <div key={s.name} className="text-primary/50">
                  #{s.name}
                </div>
              ))}
            </div>
          </div>
        );
        break;
      case "cd":
        const route = AVAILABLE_ROUTES.find((r) => r.name === arg);
        const section = PAGE_SECTIONS.find((s) => s.name === arg);
        if (route) {
          result = (
            <span className="text-green-500 italic">
              Redirecting to /{arg}...
            </span>
          );
          setTimeout(() => {
            setIsOpen(false);
            router.push(route.path);
          }, 600);
        } else if (section) {
          result = (
            <span className="text-green-500 italic">
              Scrolling to #{arg}...
            </span>
          );
          setTimeout(() => {
            setIsOpen(false);
            document
              .querySelector(section.anchor)
              ?.scrollIntoView({ behavior: "smooth" });
          }, 600);
        } else {
          result = (
            <span className="text-destructive">Target not found: {arg}</span>
          );
        }
        break;
      case "clear":
        setHistory([]);
        return;

      case "whoami":
        result = <span className="text-green-500">visitor@portfolio</span>;
        break;

      case "pwd":
        result = <span className="text-muted-foreground">{window.location.pathname}</span>;
        break;

      case "date":
        result = <span className="text-muted-foreground">{new Date().toString()}</span>;
        break;

      case "uname":
         if (arg.includes("-a")) {
            result = <span className="text-muted-foreground">Linux portfolio-os 5.15.0-generic #1 SMP Sat Feb 07 17:47:36 UTC 2026 x86_64 GNU/Linux</span>;
         } else {
            result = <span className="text-muted-foreground">PortfolioOS</span>;
         }
         break;

      case "echo":
        result = <span>{arg}</span>;
        break;

      case "history":
        result = (
            <div className="text-muted-foreground text-[10px] space-y-0.5">
                {history.map((h, i) => <div key={i}>{i + 1}  {h.input}</div>)}
            </div>
        );
        break;

      case "sudo":
        result = <span className="text-destructive font-bold">Permission denied: You are not Sachin. Try 'ask hire me'.</span>;
        break;

      case "rm":
      case "mkdir": 
      case "touch":
      case "chmod":
      case "chown":
        result = <span className="text-destructive">Error: Read-only file system.</span>;
        break;

      case "top":
      case "htop":
         result = (
             <div className="whitespace-pre font-mono text-[10px] text-green-500 leading-tight">
                {`Tasks: 13 total, 1 running, 12 sleeping, 0 stopped, 0 zombie
%Cpu(s):  5.0 us,  2.0 sy,  0.0 ni, 93.0 id,  0.0 wa,  0.0 hi,  0.0 si,  0.0 st
MiB Mem :  16000.0 total,   8000.0 free,   4500.0 used,   3500.0 buff/cache
MiB Swap:   2048.0 total,   2048.0 free,      0.0 used.  11000.0 avail Mem 

  PID USER      PR  NI    VIRT    RES    SHR S  %CPU  %MEM     TIME+ COMMAND
    1 sachin    20   0   Coffee   High   Low S  100.0  50.0   999:99 coding`}
             </div>
         );
         break;

       case "cat":
         if (arg === "about" || arg === "about.md") {
            result = <div className="leading-relaxed text-muted-foreground/90 whitespace-pre-wrap">{formatAiResponse(getSmartResponse("about"))}</div>;
         } else if (arg === "contact" || arg === "contact.txt") {
            result = <div className="leading-relaxed text-muted-foreground/90 whitespace-pre-wrap">{formatAiResponse(getSmartResponse("contact"))}</div>;
         } else if (arg === "skills" || arg === "skills.json") {
             result = <div className="leading-relaxed text-muted-foreground/90 whitespace-pre-wrap">{formatAiResponse(getSmartResponse("detailed skills"))}</div>;
         } else if (arg === "projects" || arg === "projects.md") {
             result = <div className="leading-relaxed text-muted-foreground/90 whitespace-pre-wrap">{formatAiResponse(getSmartResponse("all projects"))}</div>;
         } else if (arg === "experience" || arg === "experience.md") {
             result = <div className="leading-relaxed text-muted-foreground/90 whitespace-pre-wrap">{formatAiResponse(getSmartResponse("experience"))}</div>;
         } else if (arg === "education" || arg === "education.md") {
             result = <div className="leading-relaxed text-muted-foreground/90 whitespace-pre-wrap">{formatAiResponse(getSmartResponse("education"))}</div>;
         } else if (arg === "hero" || arg === "hero.txt" || arg === "home") {
             result = <div className="leading-relaxed text-muted-foreground/90 whitespace-pre-wrap">{formatAiResponse(getSmartResponse("who are you"))}</div>;
         } else if (!arg) {
             result = (
               <div className="space-y-2">
                 <div className="text-yellow-500">Usage: cat [file]</div>
                 <div className="text-muted-foreground/70 text-[10px]">
                   Available files:
                   <div className="pl-3 mt-1 space-y-0.5">
                     <div>• about.md - My bio</div>
                     <div>• contact.txt - Contact info</div>
                     <div>• skills.json - Tech stack</div>
                     <div>• projects.md - All projects</div>
                     <div>• experience.md - Work history</div>
                     <div>• education.md - Academic background</div>
                     <div>• hero.txt - Welcome message</div>
                   </div>
                 </div>
               </div>
             );
         } else {
             result = (
               <div className="space-y-2">
                 <div className="text-destructive">cat: {arg}: No such file or directory</div>
                 <div className="text-muted-foreground/70 text-[10px]">
                   Try one of these instead:
                   <div className="pl-3 mt-1">
                     <span className="text-primary">about</span>, <span className="text-primary">contact</span>, <span className="text-primary">skills</span>, <span className="text-primary">projects</span>, <span className="text-primary">experience</span>, <span className="text-primary">education</span>, <span className="text-primary">hero</span>
                   </div>
                 </div>
               </div>
             );
         }
         break;
       
       case "vi":
       case "vim":
       case "nano":
       case "code":
         result = <span className="text-muted-foreground">Error: Text editor not available in guest mode.</span>;
         break;

       case "reboot":
       case "exit":
          result = <span className="text-yellow-500">System rebooting... (Just kidding, try refreshing)</span>;
          break;

      default:
        // Check for smart response
        const smartResponse = handleSmartResponse(raw);
        if (smartResponse) {
          result = smartResponse;
        } else {
          // If no smart response, try search as fallback
          const autoSearchMatches = searchContent(raw, searchIndex);
          if (autoSearchMatches.length > 0) {
            result = (
              <div className="space-y-3 text-[10px]">
                {autoSearchMatches.map((match, idx) => (
                  <div key={match.id} className="space-y-1">
                    <div className="text-green-500 font-bold">
                      [{idx + 1}] Found in {match.type.toUpperCase()}
                    </div>
                    <div className="pl-3 text-primary/80">
                      {match.displayText}
                    </div>
                    <div className="pl-3 text-muted-foreground/60 italic">
                      {match.content.slice(0, 100)}...
                    </div>
                  </div>
                ))}
                {autoSearchMatches.length === 1 && (
                  <div className="pt-2 text-muted-foreground/70">
                    Do you want to go to{" "}
                    <span className="text-primary font-bold">
                      {autoSearchMatches[0].displayText}
                    </span>
                    ? <span className="text-green-500 font-bold">(Y/N)</span>
                  </div>
                )}
                {autoSearchMatches.length > 1 && (
                  <div className="pt-2 text-muted-foreground/70">
                    Type <span className="text-primary font-bold">1</span>-
                    <span className="text-primary font-bold">
                      {autoSearchMatches.length}
                    </span>{" "}
                    to select, or{" "}
                    <span className="text-primary font-bold">n</span> to cancel
                  </div>
                )}
              </div>
            );
            if (autoSearchMatches.length >= 1) {
              if (autoSearchMatches.length === 1) {
                setPendingNavigation({
                  anchor: autoSearchMatches[0].anchor,
                  displayText: autoSearchMatches[0].displayText,
                });
              } else {
                setPendingMatches(autoSearchMatches);
              }
            }
          } else {
             // Fallback for completely unknown commands
             result = (
               <div className="space-y-2">
                 <div className="text-red-400 font-bold">Cmd not found 🚫</div>
                 <div className="text-muted-foreground/80 text-[9px]">
                   I don't know that command yet. Try <span className="text-primary">help</span> to see what I can do!
                 </div>
               </div>
             );
          }
        }
    }

    setHistory((prev) => [
      ...prev,
      { id: Date.now(), input: raw, output: result },
    ]);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      executeCommand(input).catch(console.error);
      setInput("");
    } else if (e.key === "Tab" || e.key === "ArrowRight") {
      if (suggestion && suggestion !== input) {
        e.preventDefault();
        setInput(suggestion);
      }
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      if (commandHistory.length > 0) {
        setHistoryIndex((prev) => {
          const newIndex =
            prev === null ? commandHistory.length - 1 : Math.max(prev - 1, 0);
          setInput(commandHistory[newIndex]);
          return newIndex;
        });
      }
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      if (commandHistory.length > 0) {
        setHistoryIndex((prev) => {
          if (prev === null) return null;
          const newIndex = Math.min(prev + 1, commandHistory.length - 1);
          setInput(commandHistory[newIndex] || "");
          return newIndex;
        });
      }
    } else if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
      // Cmd+K (Mac) or Ctrl+K (Win/Linux) clears terminal
      e.preventDefault();
      setHistory([]);
    }
  };

  // Auto-scroll disabled - user can scroll manually
  // useEffect(() => {
  //   if (terminalRef.current) {
  //     setTimeout(() => {
  //       terminalRef.current!.scrollTop = terminalRef.current!.scrollHeight;
  //     }, 10);
  //   }
  // }, [history]);

  // Lock body scroll when terminal is open
  useEffect(() => {
    if (isOpen && !isMinimized) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen, isMinimized]);

  if (isAdminPage || isBlogPage || isConversion) return null;

  return (
    <>
      {/* Launcher Button: Show if closed OR minimized */}
      {(!isOpen || isMinimized) && (
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => {
            setIsOpen(true);
            setIsMinimized(false);
            setIsMaximized(false);
            setTimeout(() => inputRef.current?.focus(), 150);
          }}
          className="fixed bottom-24 right-5 md:bottom-6 md:right-6 z-[60] p-3 sm:p-4 rounded-xl sm:rounded-2xl bg-primary text-primary-foreground shadow-2xl hidden md:flex items-center gap-2 sm:gap-3 group border border-primary/20"
        >
          <TerminalIcon className="w-4 h-4 sm:w-5 sm:h-5" />
          <span className="max-w-0 overflow-hidden group-hover:max-w-xs transition-all duration-500 font-mono text-[9px] sm:text-[10px] font-black tracking-[0.2em]">
            TERMINAL
          </span>
        </motion.button>
      )}

      <AnimatePresence>
        {isOpen && !isMinimized && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsOpen(false)}
              className="fixed inset-0 z-[70] bg-background/40 backdrop-blur-[2px]"
            />

            <motion.div
              initial={{ opacity: 0, y: 30, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 30, scale: 0.95 }}
              onClick={(e) => e.stopPropagation()}
              className={`fixed z-[80] flex flex-col overflow-hidden rounded-2xl border border-border bg-background/95 backdrop-blur-2xl shadow-[0_32px_64px_-16px_rgba(0,0,0,0.5)] font-mono
                ${
                  isMaximized
                    ? "inset-2 w-[96vw] h-[90vh] left-1 right-1 top-4 bottom-4"
                    : "left-2 right-2 bottom-24 md:bottom-24 md:right-6 md:left-auto w-[calc(100vw-1rem)] md:w-[450px] h-[380px] md:h-[400px] max-h-[65vh] md:max-h-none"
                }
                max-w-full
              `}
            >
              {/* Terminal Header */}
              <div className="flex items-center justify-between px-4 py-2.5 border-b border-border bg-muted/30">
                <div className="flex items-center gap-2">
                  <div className="flex gap-1.5">
                    <button
                      className="w-2.5 h-2.5 rounded-full bg-red-500 focus:outline-none"
                      title="Close"
                      onClick={() => setIsOpen(false)}
                    />
                    <button
                      className="w-2.5 h-2.5 rounded-full bg-yellow-500 focus:outline-none"
                      title="Minimize"
                      onClick={() => setIsMinimized(true)}
                    />
                    <button
                      className="w-2.5 h-2.5 rounded-full bg-green-500 focus:outline-none"
                      title="Maximize"
                      onClick={() => setIsMaximized((v) => !v)}
                    />
                  </div>
                  <span className="text-[10px] font-bold opacity-30 uppercase tracking-widest ml-2">
                    sachin@portfolio
                  </span>
                </div>
                <button
                  onClick={() => setIsOpen(false)}
                  className="p-1 hover:bg-muted rounded transition-colors group"
                >
                  <X className="w-4 h-4 opacity-30 group-hover:opacity-100" />
                </button>
              </div>

              {/* Viewport */}
              <div
                ref={terminalRef}
                onWheel={(e) => e.stopPropagation()}
                className="flex-1 overflow-y-auto p-5 text-[13px] space-y-5 scrollbar-hide overscroll-contain"
              >
                <div className="opacity-30 text-[10px] mb-4 tracking-tighter uppercase italic">
                  // Connection established. Ready for input.
                  <br />
                  // Type <span className="text-primary font-bold">
                    help
                  </span>{" "}
                  to see available commands.
                </div>

                {history.map((item, idx) => (
                  <div
                    key={item.id}
                    className="space-y-2 animate-in fade-in slide-in-from-left-1 duration-300"
                  >
                    <div className="flex items-center gap-2 text-primary/70">
                      <Hash className="w-3 h-3" />
                      <span className="font-bold tracking-tight">
                        {item.input}
                      </span>
                    </div>
                    <div className="pl-5 opacity-90 leading-relaxed border-l border-primary/10 ml-1.5 font-medium">
                      {/* Show output directly - streaming text will appear here as it updates */}
                      {item.output}

                      {/* Show a subtle pulsing cursor if this is the active AI response being generated */}
                      {isAiLoading && idx === history.length - 1 && (
                        <span className="inline-block w-1.5 h-3 bg-cyan-500/50 ml-1 animate-pulse" />
                      )}
                    </div>
                  </div>
                ))}

                {/* --- SMART INPUT LINE --- */}
                <div className="relative flex items-center gap-2.5 group">
                  <ChevronRight className="w-3.5 h-3.5 text-primary" />

                  {/* Ghost Hint Layer: This aligns perfectly because of Monospace */}
                  {suggestion &&
                    suggestion
                      .toLowerCase()
                      .startsWith(input.toLowerCase()) && (
                      <div className="absolute left-[26px] pointer-events-none flex whitespace-pre opacity-30">
                        {/* Typed part (Invisible) */}
                        <span className="invisible">{input}</span>
                        {/* Suggestion Suffix (Visible) */}
                        <span>{suggestion.slice(input.length)}</span>
                      </div>
                    )}

                  <input
                    ref={inputRef}
                    type="text"
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={handleKeyDown}
                    disabled={isAiLoading}
                    className={`flex-1 bg-transparent outline-none border-none p-0 focus:ring-0 text-foreground ${
                      isAiLoading
                        ? "opacity-20 cursor-not-allowed"
                        : "opacity-100"
                    }`}
                    spellCheck={false}
                    autoComplete="off"
                    autoFocus
                    placeholder={
                      isAiLoading
                        ? "Waiting for AI response..."
                        : pendingNavigation || pendingMatches.length > 0
                        ? "> "
                        : ""
                    }
                  />

                  {suggestion && suggestion !== input && (
                    <div className="flex items-center gap-1.5 opacity-0 group-focus-within:opacity-30 transition-opacity">
                      <Command className="w-3 h-3" />
                      <span className="text-[9px] font-bold tracking-tighter">
                        TAB
                      </span>
                    </div>
                  )}
                </div>

                {/* Navigation Prompt Indicator */}
                {pendingNavigation && (
                  <div className="text-[9px] text-green-500 font-bold mt-2 animate-pulse">
                    Do you want to go to {pendingNavigation.displayText}?{" "}
                    <span className="text-primary">(Y/N)</span>
                  </div>
                )}
                {pendingMatches.length > 0 && (
                  <div className="text-[9px] text-green-500 font-bold mt-2 animate-pulse">
                    Select option <span className="text-primary">1</span>-
                    <span className="text-primary">
                      {pendingMatches.length}
                    </span>
                    , or <span className="text-primary">N</span> to cancel
                  </div>
                )}
              </div>

              {/* Footer Bar */}
              <div className="px-4 py-2 border-t border-border bg-muted/20 flex justify-between items-center text-[9px] font-bold opacity-30 tracking-widest">
                <span>TERMINAL_V2.0</span>
                <span>CMD+K TO CLEAR</span>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
}
